import ical, { ICalCalendarMethod, ICalEventRepeatingFreq, ICalWeekday, } from 'ical-generator';
import { getJamUrl } from './jamHelpers.js';
const utcDateFromYmd = (yyyyMmDd) => {
    const [y, m, d] = yyyyMmDd.split('-').map(Number);
    return new Date(Date.UTC(y, m - 1, d));
};
const exclusiveEndAfterInclusiveDay = (yyyyMmDd) => {
    const dt = utcDateFromYmd(yyyyMmDd);
    dt.setUTCDate(dt.getUTCDate() + 1);
    return dt;
};
const originFromUrl = (url) => {
    if (!url) {
        return undefined;
    }
    try {
        return new URL(url).origin;
    }
    catch {
        return undefined;
    }
};
/** Prefer a joinable place: physical address, jam URL, then external event URL. */
const eventLocation = (post, event, postUrl) => {
    var _a, _b, _c;
    const physical = (_b = (_a = event.physicalLocation) === null || _a === void 0 ? void 0 : _a.text) === null || _b === void 0 ? void 0 : _b.trim();
    if (physical) {
        return physical;
    }
    if (event.jam) {
        const jamUrl = getJamUrl(post.id, originFromUrl(postUrl));
        if (jamUrl.startsWith('http')) {
            return jamUrl;
        }
    }
    return ((_c = event.url) === null || _c === void 0 ? void 0 : _c.trim()) || undefined;
};
const repeatingFreq = {
    DAILY: ICalEventRepeatingFreq.DAILY,
    WEEKLY: ICalEventRepeatingFreq.WEEKLY,
    MONTHLY: ICalEventRepeatingFreq.MONTHLY,
};
const repeatingWeekday = {
    MO: ICalWeekday.MO,
    TU: ICalWeekday.TU,
    WE: ICalWeekday.WE,
    TH: ICalWeekday.TH,
    FR: ICalWeekday.FR,
    SA: ICalWeekday.SA,
    SU: ICalWeekday.SU,
};
const repeatingOptions = (event) => {
    var _a, _b, _c;
    const recurrence = event.recurrence;
    if (!recurrence)
        return undefined;
    const exclude = ((_a = event.exceptions) !== null && _a !== void 0 ? _a : [])
        .filter((exception) => exception.cancelled)
        .map((exception) => new Date(exception.recurrenceId));
    return {
        freq: repeatingFreq[recurrence.freq],
        interval: (_b = recurrence.interval) !== null && _b !== void 0 ? _b : 1,
        ...(((_c = recurrence.byDay) === null || _c === void 0 ? void 0 : _c.length)
            ? { byDay: recurrence.byDay.map((day) => repeatingWeekday[day]) }
            : {}),
        ...(recurrence.until ? { until: new Date(recurrence.until) } : {}),
        ...(recurrence.count ? { count: recurrence.count } : {}),
        ...(exclude.length ? { exclude } : {}),
    };
};
export const buildEventIcs = (post, options) => {
    var _a, _b, _c, _d, _e, _f, _g;
    if (post.type !== 'event' || ((_a = post.data) === null || _a === void 0 ? void 0 : _a.type) !== 'event') {
        return null;
    }
    const event = post.data;
    const calendar = ical({
        prodId: '//OpenPeeps//Event//EN',
        method: ICalCalendarMethod.PUBLISH,
        scale: 'GREGORIAN',
    });
    const link = (_b = event.url) !== null && _b !== void 0 ? _b : options === null || options === void 0 ? void 0 : options.postUrl;
    const summary = ((_c = event.name) === null || _c === void 0 ? void 0 : _c.trim()) || 'Event';
    const description = (_d = event.content) === null || _d === void 0 ? void 0 : _d.trim();
    const location = eventLocation(post, event, options === null || options === void 0 ? void 0 : options.postUrl);
    const repeating = repeatingOptions(event);
    if (event.wholeDay) {
        const startYmd = event.start.slice(0, 10);
        const lastInclusiveYmd = (_f = (_e = event.end) === null || _e === void 0 ? void 0 : _e.slice(0, 10)) !== null && _f !== void 0 ? _f : startYmd;
        calendar.createEvent({
            id: `openpeeps-event-${post.id}@openpeepshq`,
            allDay: true,
            start: utcDateFromYmd(startYmd),
            end: exclusiveEndAfterInclusiveDay(lastInclusiveYmd),
            summary,
            ...(description ? { description } : {}),
            ...(location ? { location } : {}),
            ...(link ? { url: link } : {}),
            ...(repeating ? { repeating } : {}),
        });
    }
    else {
        const start = new Date(event.start);
        const end = event.end
            ? new Date(event.end)
            : new Date(start.getTime() + 60 * 60 * 1000);
        calendar.createEvent({
            id: `openpeeps-event-${post.id}@openpeepshq`,
            start,
            end,
            summary,
            ...(description ? { description } : {}),
            ...(location ? { location } : {}),
            ...(link ? { url: link } : {}),
            ...(repeating ? { repeating } : {}),
        });
    }
    for (const exception of (_g = event.exceptions) !== null && _g !== void 0 ? _g : []) {
        if (exception.cancelled || !exception.start)
            continue;
        const start = new Date(exception.start);
        const end = exception.end
            ? new Date(exception.end)
            : new Date(start.getTime() + 60 * 60 * 1000);
        calendar.createEvent({
            id: `openpeeps-event-${post.id}-${exception.recurrenceId}@openpeepshq`,
            start,
            end,
            summary,
            ...(description ? { description } : {}),
            ...(location ? { location } : {}),
            ...(link ? { url: link } : {}),
            recurrenceId: new Date(exception.recurrenceId),
        });
    }
    return calendar.toString() + '\r\n';
};
